// Adapter for the new V2 package availability APIs (get-package-details + get-package-calendar).
//
// The legacy /get-package-available returned a single merged payload that the order-creation drawer
// consumed in 4 call sites. The new V2 backend splits this into two endpoints:
//   - get-package-details : package info + workers + price + availabilityScope (static per package)
//   - get-package-calendar: provider days, and slots for a single selected date
//
// This module exposes two helpers that produce the legacy-shaped PackageServiceResponse so the
// existing components keep working without a full rewrite:
//   - loadPackageAvailabilityV2: initial load (details + calendar in parallel)
//   - loadPackageCalendarV2    : selection / date changes (calendar only, reusing existing details)

import { ticketApi } from './TicketApi'
import {
  DatePackageAvailable,
  PackageAvailableCalendarDay,
  PackageAvailableCalendarRequest,
  PackageAvailableCalendarResponse,
  PackageAvailableDetails,
  PackageAvailableDetailsRequest,
  PackageAvailableDetailsResponse,
  PackageAvailableDetailsWorker,
  PackageInfo,
  PackageServiceResponse,
  Worker,
  preSelectSP as PreSelectSP,
} from '../models/OrderDrawerModel'
import { pickRouteOptimizationCfg } from 'app/utils/helpers/OrderDrawerHelpers'

export interface LoadPackageAvailabilityV2Request {
  packageWorkflowId: number
  countryId: number
  packageId: number
  majorId: number
  clientTimeOffset: number
  companyId?: string
  customerId?: string
  customerPhone?: string
  customerLat?: number
  customerLng?: number
  zoneIds?: number[]
  spIds?: string[]
  numbOfSelectWorker?: number
  workerId?: string
  selectedDate?: string
  sourceType?: number
  startDay?: number
  limitDays?: number
}

// Pagination is lazy: the initial calendar fetch covers the first visible month grid only
// (~35-42 days), and the user navigating to a later month triggers a delta fetch from
// `loadedUntilDay` for the next month's grid. See `computeCalendarLimitDays`.

const MS_PER_DAY = 86_400_000

const startOfLocalDay = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate())

// Last visible cell of the datepicker grid for `displayedMonth`. Mirrors react-datepicker's
// 6-row grid with calendarStartDay = 1 (Monday). Always 42 cells (6×7).
const getCalendarGridEnd = (displayedMonth: Date): Date => {
  const firstOfMonth = new Date(displayedMonth.getFullYear(), displayedMonth.getMonth(), 1)
  const offsetToMonday = (firstOfMonth.getDay() + 6) % 7
  const firstCell = new Date(firstOfMonth)
  firstCell.setDate(firstOfMonth.getDate() - offsetToMonday)
  const lastCell = new Date(firstCell)
  lastCell.setDate(firstCell.getDate() + 6 * 7 - 1) // 41 days after
  return lastCell
}

/** True when `day` falls in the Monday–Sunday week that contains the last day of `month`. */
const isInLastWeekOfMonth = (day: Date, month: Date): boolean => {
  const lastDayOfMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0)
  const offsetToMonday = (lastDayOfMonth.getDay() + 6) % 7
  const lastWeekMonday = startOfLocalDay(
    new Date(lastDayOfMonth.getFullYear(), lastDayOfMonth.getMonth(), lastDayOfMonth.getDate() - offsetToMonday)
  )
  const lastWeekSunday = startOfLocalDay(
    new Date(lastWeekMonday.getFullYear(), lastWeekMonday.getMonth(), lastWeekMonday.getDate() + 6)
  )
  const d = startOfLocalDay(day)
  return d >= lastWeekMonday && d <= lastWeekSunday
}

// Days from `today` (inclusive) needed so the displayed month's grid is fully covered by
// the backend response. Used as `limitDays` (or to compute the delta on month navigation).
// When `today` is in the last week of the displayed month, also cover the next month's
// grid so mid/late next-month dates are available without waiting for onMonthChange.
export const computeCalendarLimitDays = (today: Date, displayedMonth: Date): number => {
  const start = startOfLocalDay(today)
  let end = startOfLocalDay(getCalendarGridEnd(displayedMonth))

  if (isInLastWeekOfMonth(start, displayedMonth)) {
    const nextMonth = new Date(displayedMonth.getFullYear(), displayedMonth.getMonth() + 1, 1)
    const nextMonthEnd = startOfLocalDay(getCalendarGridEnd(nextMonth))
    if (nextMonthEnd > end) {
      end = nextMonthEnd
    }
  }

  const diffDays = Math.ceil((end.getTime() - start.getTime()) / MS_PER_DAY) + 1
  return Math.max(1, diffDays)
}

export const computeCalendarDelta = (
  displayedMonth: Date,
  loadedUntilDay?: number
): {startDay: number; limitDays: number; requiredDays: number} | null => {
  const requiredDays = computeCalendarLimitDays(new Date(), displayedMonth)
  const loadedSoFar = loadedUntilDay ?? 0
  if (requiredDays <= loadedSoFar) return null
  return {startDay: loadedSoFar, limitDays: requiredDays - loadedSoFar, requiredDays}
}

const mapCalendarDayToLegacy = (day: PackageAvailableCalendarDay): DatePackageAvailable => ({
  dateCheck: day.dateCheck as unknown as Date, // Frontend re-parses via parseDateCheck helpers.
  isAvailable: day.isAvailable,
  isRamadanTime: day.isRamadanTime,
  dayIndex: day.dayIndex,
  capacity: day.capacity,
  isExistedVisit: day.isExistedVisit,
  numberVisit: day.numberVisit,
  listTimes: (day.timeSlots || []).map(t => ({
    start: t.start,
    end: t.end,
    capacity: t.capacity,
    isAvailable: t.isAvailable,
  })),
  spAvaialble: (day.providers || []).map(p => ({
    spId: p.spId,
    spName: p.spName,
    availableCapacity: 0,
    capacity: 0,
    capacityUsed: 0,
    visitReminder: p.visitReminder,
    routeOptimizationCfg: p.routeOptimizationCfg ?? null,
    listTimes: (p.timeSlots || []).map(t => ({
      start: t.start,
      end: t.end,
      capacity: t.capacity,
      isAvailable: t.isAvailable,
    })),
  })),
})

const buildPreSelectSP = (
  details: PackageAvailableDetails | null,
  calendar: PackageAvailableCalendarResponse | null,
  countryId: number
): PreSelectSP | undefined => {
  const provider = calendar?.preSelectProvider
  if (!provider) return undefined
  return {
    spId: provider.spId,
    numberDayAvailable: provider.numberDayAvailable as 4, // legacy field literal-typed in OrderDrawerModel
    dayAvailable: (calendar?.days || []).map(mapCalendarDayToLegacy),
    isShowVat: provider.isShowVat,
    countryId: countryId?.toString() ?? '',
    companyId: provider.companyId,
    companyNameEn: provider.companyNameEn,
    companyNameAr: provider.companyNameAr,
    companyLogoUrl: provider.companyLogoUrl,
    visitReminder: provider.visitReminder ?? '',
    spName: provider.spName,
    packagePrice: details?.packagePrice ?? null,
    packagePriceWithVat: details?.packagePriceWithVat ?? null,
    routeOptimizationCfg: pickRouteOptimizationCfg(provider),
  }
}

// Build a PackageInfo from details alone (no calendar yet). Used for progressive rendering:
// the package metadata + workers + pricing land in the UI as soon as get-package-details resolves,
// without waiting for the heavier get-package-calendar call.
export const buildLegacyPackageInfoFromDetails = (
  details: PackageAvailableDetails | null
): PackageInfo[] => buildLegacyPackageInfo(details, null, 0)

// Build the calendar-derived preSelectSP once /get-package-calendar has resolved.
// `details` is optional and only used to mirror packagePrice on the preSelectSP shape.
export const buildPreSelectSPFromCalendar = (
  calendar: PackageAvailableCalendarResponse | null,
  details: PackageAvailableDetails | null,
  countryId: number
): PreSelectSP | undefined => buildPreSelectSP(details, calendar, countryId)

const mapDetailsWorkerToLegacy = (w: PackageAvailableDetailsWorker): Worker => ({
  workerId: w.workerId,
  spId: w.spId,
  workerNameAr: w.workerNameAr,
  workerNameEn: w.workerNameEn,
  startDate: undefined,
  personalImageUrl: w.personalImageUrl,
  isFavoriteWorker: w.isFav,
  // Per-worker calendars are no longer pre-bundled by the backend; calendar refetch fills these on selection.
  listAvailableDay: [],
  visitReminder: w.visitReminder ?? undefined,
  routeOptimizationCfg: pickRouteOptimizationCfg(w),
})

const buildLegacyPackageInfo = (
  details: PackageAvailableDetails | null,
  calendar: PackageAvailableCalendarResponse | null,
  countryId: number
): PackageInfo[] => {
  if (!details) return []
  const visitDurationFallback = details.visitDuration
  const isShowDeliveryTime =
    calendar?.isShowDeliveryTime ??
    calendar?.preSelectProvider?.isShowDeliveryTime ??
    details.isShowDeliveryTime
  return [
    {
      id: details.id,
      spId: undefined,
      visitDuration: visitDurationFallback,
      numberOfVisitRecurrence: details.numberOfVisitRecurrence ?? undefined,
      shiftType: details.shiftType ?? 0,
      capacityPercentage: details.capacityPercentage ?? 0,
      numberOfWeeks: details.numberOfWeeks,
      multiWorker: {
        workerNum: details.multiWorker.workerNum,
        isApply: details.multiWorker.isApply,
        isRequireMatchWorkerNumber: details.multiWorker.isRequireMatchWorkerNumber,
        minWorkerAllow: details.multiWorker.minWorkerAllow,
      },
      applyAutoDiscount: details.applyAutoDiscount,
      numberOfVisitPerWeek: details.numberOfVisitPerWeek,
      isAutoDiscountShowTimer: details.isAutoDiscountShowTimer,
      autoDiscountExpDay: details.autoDiscountExpDay,
      discountEndDate: null,
      discountDay: details.discountDay,
      isAvailable: true,
      timeRange: undefined as any,
      preSelectSP: buildPreSelectSP(details, calendar, countryId),
      isRenewPackageFavoriteWorker: details.isRenewPackageFavoriteWorker,
      WorkerId: undefined,
      isShowNumberOfWorker: details.isShowNumberOfWorker,
      isShowWorkerQuestion: details.isShowWorkerQuestion,
      numberOfWorker: details.numberOfWorker,
      isSPAvailable: details.isSPAvailable,
      subServiceId: details.subServiceId,
      isAutoDiscount: details.isAutoDiscount,
      calcForSPOnly: details.calcForSPOnly,
      isShowFavWorker: details.isShowFavWorker,
      hasFavWorkerAvailable: details.hasFavWorkerAvailable,
      workers: details.workers.map(mapDetailsWorkerToLegacy),
      hasUnratedWorker: details.hasUnratedWorker,
      packagePrice: details.packagePrice,
      packagePriceWithVat: details.packagePriceWithVat,
      percentVAT: details.percentVAT,
      titleAr: details.titleAr,
      titleEn: details.titleEn,
      packageServicePriceSetId: details.packageServicePriceSetId,
      packageServicePriceSetDetailId: details.packageServicePriceSetDetailId,
      isRefund: details.isRefund,
      reScheduleLimit: details.reScheduleLimit ?? null,
      isShowDeliveryTime,
      visitReminder: details.visitReminder ?? undefined,
    },
  ]
}

const buildDetailsRequest = (req: LoadPackageAvailabilityV2Request): PackageAvailableDetailsRequest => ({
  packageWorkflowId: req.packageWorkflowId,
  countryId: req.countryId,
  packageId: req.packageId,
  majorId: req.majorId,
  companyId: req.companyId,
  customerId: req.customerId,
  zoneIds: req.zoneIds,
  spIds: req.spIds,
  numbOfSelectWorker: req.numbOfSelectWorker,
  workerId: req.workerId,
  sourceType: req.sourceType,
})

// Calendar request keeps the base scope (package + country + zones) and forwards the
// provider/worker filters when they're set:
//   - spIds  -> busIds (backend parameter name on /get-package-calendar)
//   - workerId (singular, when the user has picked a specific worker)
// On day-click, customerLat/customerLng are also forwarded so the per-day slot resolution
// can be scored against the customer location.
const buildCalendarRequest = (
  req: LoadPackageAvailabilityV2Request
): PackageAvailableCalendarRequest => {
  const out: PackageAvailableCalendarRequest = {
    packageWorkflowId: req.packageWorkflowId,
    countryId: req.countryId,
    packageId: req.packageId,
    majorId: req.majorId,
    clientTimeOffset: req.clientTimeOffset,
    zoneIds: req.zoneIds,
  }
  if (req.spIds && req.spIds.length > 0) {
    out.busIds = req.spIds
  }
  if (req.workerId) {
    out.workerId = req.workerId
  }
  // Caller is expected to provide limitDays sized for the visible month. Falling back to
  // a single month window prevents accidental full-year fetches when wiring is incomplete.
  out.limitDays =
    req.limitDays ?? computeCalendarLimitDays(new Date(), new Date())
  if (req.startDay !== undefined && req.startDay > 0) {
    out.startDay = req.startDay
  }
  if (req.selectedDate) {
    out.selectedDate = req.selectedDate
    if (req.customerLat !== undefined) {
      out.customerLat = req.customerLat
    }
    if (req.customerLng !== undefined) {
      out.customerLng = req.customerLng
    }
  }
  out.numbOfSelectWorker = req.numbOfSelectWorker
  out.customerPhone = req.customerPhone;
  return out
}

// Local-day key for deduping calendar entries whose dateCheck may be a Date or a date string.
const calendarDayKey = (d: any): string => {
  if (d instanceof Date) {
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
  }
  const parsed = new Date(d)
  if (!isNaN(parsed.getTime())) {
    return `${parsed.getFullYear()}-${parsed.getMonth()}-${parsed.getDate()}`
  }
  return String(d)
}

const sortCalendarDays = (days: DatePackageAvailable[]): DatePackageAvailable[] =>
  days.sort((a, b) => {
    const ad = a.dateCheck instanceof Date ? a.dateCheck : new Date(a.dateCheck)
    const bd = b.dateCheck instanceof Date ? b.dateCheck : new Date(b.dateCheck)
    return ad.getTime() - bd.getTime()
  })

// Merge a freshly fetched page of calendar days into an existing list, keyed by `dateCheck`.
// New days replace existing entries at the same date (so time slots stay current).
export const mergeCalendarDays = (
  existing: DatePackageAvailable[] = [],
  incoming: DatePackageAvailable[] = []
): DatePackageAvailable[] => {
  if (!incoming.length) return existing
  const map = new Map<string, DatePackageAvailable>()
  existing.forEach(d => map.set(calendarDayKey(d.dateCheck), d))
  incoming.forEach(d => map.set(calendarDayKey(d.dateCheck), d))
  return sortCalendarDays(Array.from(map.values()))
}

// Merge several day lists (one per provider/worker) into a single calendar view: a date is
// available when ANY list has it available; later lists override the other fields at the
// same date. Used by the reschedule modal's pooled ("Based on Availability") calendar.
export const mergeWorkerDayLists = (
  lists: (DatePackageAvailable[] | undefined)[]
): DatePackageAvailable[] => {
  const map = new Map<string, DatePackageAvailable>()
  lists.forEach((days) => {
    (days || []).forEach((day) => {
      const key = calendarDayKey(day.dateCheck)
      const existing = map.get(key)
      map.set(
        key,
        existing
          ? {
              ...existing,
              ...day,
              isAvailable: existing.isAvailable || day.isAvailable,
              dateCheck: existing.dateCheck, // keep the first-seen dateCheck format
            }
          : {...day}
      )
    })
  })
  return sortCalendarDays(Array.from(map.values()))
}

// Map calendar response days into the legacy DatePackageAvailable shape (used for merging).
export const mapCalendarDaysToLegacy = (
  calendar: PackageAvailableCalendarResponse | null
): DatePackageAvailable[] => (calendar?.days || []).map(mapCalendarDayToLegacy)

// Initial load on package selection: call details + calendar in parallel and produce the legacy shape.
export const loadPackageAvailabilityV2 = async (
  req: LoadPackageAvailabilityV2Request
): Promise<{
  legacy: PackageServiceResponse
  details: PackageAvailableDetails | null
  calendar: PackageAvailableCalendarResponse | null
}> => {
  const [detailsResp, calendarResp] = await Promise.all([
    ticketApi.getPackageDetails(buildDetailsRequest(req)),
    ticketApi.getPackageCalendar(buildCalendarRequest(req)),
  ])
  const details = detailsResp.data?.packageInfo ?? null
  const calendar = calendarResp.data ?? null
  return {
    details,
    calendar,
    legacy: {
      packageInfo: buildLegacyPackageInfo(details, calendar, req.countryId),
      favWorkerIds: details?.workers?.filter(w => w.isFav).map(w => w.workerId) ?? [],
    },
  }
}

// Progressive variant: returns the two API promises so the caller can render each phase independently.
// Use this in the package-selection flow where the package summary should appear as soon as details
// arrives, and the calendar (Date step) should unlock when calendar arrives.
export const startPackageAvailabilityV2 = (
  req: LoadPackageAvailabilityV2Request
): {
  detailsPromise: Promise<PackageAvailableDetails | null>
  calendarPromise: Promise<PackageAvailableCalendarResponse | null>
} => {
  const detailsPromise = ticketApi.getPackageDetails(buildDetailsRequest(req)).then(r => r.data?.packageInfo ?? null)
  const calendarPromise = ticketApi.getPackageCalendar(buildCalendarRequest(req)).then(r => r.data ?? null)
  return { detailsPromise, calendarPromise }
}

// Subsequent selection changes (worker / number-of-workers / date click): only refetch calendar.
// Caller should reuse the cached details obtained from loadPackageAvailabilityV2.
export const loadPackageCalendarV2 = async (
  req: LoadPackageAvailabilityV2Request,
  cachedDetails: PackageAvailableDetails | null
): Promise<{
  legacy: PackageServiceResponse
  calendar: PackageAvailableCalendarResponse | null
}> => {
  const calendarResp = await ticketApi.getPackageCalendar(buildCalendarRequest(req))
  const calendar = calendarResp.data ?? null
  return {
    calendar,
    legacy: {
      packageInfo: buildLegacyPackageInfo(cachedDetails, calendar, req.countryId),
      favWorkerIds: cachedDetails?.workers?.filter(w => w.isFav).map(w => w.workerId) ?? [],
    },
  }
}

// Fetch a single calendar page (delta) for lazy month-navigation expansion.
// Caller supplies `startDay` + `limitDays` so the backend returns only the new range;
// the response days are returned in the legacy DatePackageAvailable shape ready to merge.
export const loadPackageCalendarPageV2 = async (
  req: LoadPackageAvailabilityV2Request
): Promise<{
  days: DatePackageAvailable[]
  calendar: PackageAvailableCalendarResponse | null
}> => {
  const calendarResp = await ticketApi.getPackageCalendar(buildCalendarRequest(req))
  const calendar = calendarResp.data ?? null
  return {
    calendar,
    days: mapCalendarDaysToLegacy(calendar),
  }
}
